import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Camera, ChevronRight, Loader2, TriangleAlert, X } from '@/components/icons'
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer'
import { Button } from './ui/button'
import { FlightFolioScanResult } from './FlightFolioScanResult'
import { useAircraftGetAll } from '../lib/hooks/aircraft'
import {
  generateAircraftFolioScanS3Key,
  uploadFileWithPresignedPost,
  useS3GetUploadPost,
} from '../lib/hooks/s3'
import {
  useFlightFolioScanCreate,
  useFlightFolioScanDelete,
  useFlightFolioScanGetById,
  useFlightFolioScanRetry,
} from '../lib/hooks/flightFolioScan'
import {
  folioScanMediaTypes,
  type FolioExtraction,
  type FolioScanMediaType,
} from '@@api/flightFolioScan.shared'
import { hapticLight } from '@/lib/haptics'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

interface FlightFolioScanDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Preselected aircraft; omit to let the pilot pick one (home quick action) */
  aircraftId?: string
  /** Open an existing scan directly (aircraft flight-logs list) */
  scanId?: string
  /** When set, a completed scan offers "Apply to log" with the extraction */
  onApply?: (extracted: FolioExtraction) => void
}

/**
 * Text-only paper flight folio scanner: the pilot photographs the paper
 * Flight Folio Technical Log sheet, the photo is uploaded to S3 and the
 * backend extracts every field with Claude vision. Extraction runs in the
 * background, so after `create` this drawer polls the scan until it flips to
 * completed/failed, then renders the extracted data as read-only tables.
 */
export function FlightFolioScanDrawer({
  open,
  onOpenChange,
  aircraftId,
  scanId,
  onApply,
}: FlightFolioScanDrawerProps) {
  const [selectedAircraftId, setSelectedAircraftId] = useState<string | undefined>(aircraftId)
  const [activeScanId, setActiveScanId] = useState<string | undefined>(scanId)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const libraryInputRef = useRef<HTMLInputElement>(null)

  const { data: aircraftList = [] } = useAircraftGetAll()
  const getUploadPost = useS3GetUploadPost()
  const createScan = useFlightFolioScanCreate()
  const retryScan = useFlightFolioScanRetry()
  const deleteScan = useFlightFolioScanDelete()
  const { data: scan } = useFlightFolioScanGetById(activeScanId)

  // Reset per-open state so a reopened drawer starts fresh
  useEffect(() => {
    if (open) {
      setSelectedAircraftId(aircraftId)
      setActiveScanId(scanId)
      setUploadProgress(null)
      setUploadError(null)
    }
  }, [open, aircraftId, scanId])

  const isUploading = uploadProgress !== null || createScan.isPending

  const handleFileSelected = async (file: File | undefined) => {
    if (!file || !selectedAircraftId) return
    if (!folioScanMediaTypes.some((t) => t === file.type)) {
      toast.error('Please use a JPEG or PNG photo')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Photo is too large (max 20MB)')
      return
    }

    setUploadError(null)
    setUploadProgress(0)
    try {
      const s3Key = generateAircraftFolioScanS3Key(selectedAircraftId, file.name)
      await uploadFileWithPresignedPost(file, s3Key, getUploadPost.mutateAsync, setUploadProgress)
      const { id } = await createScan.mutateAsync({
        aircraftId: selectedAircraftId,
        s3Key,
        contentType: file.type as FolioScanMediaType,
      })
      setActiveScanId(id)
    } catch (error) {
      console.error('Folio upload failed:', error)
      setUploadError(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setUploadProgress(null)
    }
  }

  const handleRetry = async () => {
    if (!activeScanId) return
    try {
      await retryScan.mutateAsync({ id: activeScanId })
    } catch (error) {
      console.error('Folio retry failed:', error)
      toast.error('Could not retry the scan')
    }
  }

  const handleDelete = async () => {
    if (!activeScanId) return
    try {
      await deleteScan.mutateAsync({ id: activeScanId })
      onOpenChange(false)
    } catch (error) {
      console.error('Folio delete failed:', error)
      toast.error('Could not delete the scan')
    }
  }

  const renderBody = () => {
    // 1. Aircraft picker — only when opened without an aircraft
    if (!selectedAircraftId) {
      return (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-content-primary-subtle leading-5">
            Which aircraft is this folio for?
          </p>
          <div className="flex flex-col">
            {aircraftList.map((aircraft, index) => (
              <div key={aircraft.id}>
                {index > 0 && <hr className="border-border-L1" />}
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 py-3"
                  onClick={() => {
                    hapticLight()
                    setSelectedAircraftId(aircraft.id)
                  }}
                >
                  <div className="flex min-w-0 flex-col items-start">
                    <p className="text-base font-semibold text-content-primary-bold leading-6">
                      {aircraft.registration}
                    </p>
                    <p className="text-sm text-content-primary-subtle leading-5">{aircraft.type}</p>
                  </div>
                  <ChevronRight className="size-4 shrink-0 text-content-primary-subtle" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )
    }

    // 2/3. Photo capture + upload
    if (!activeScanId) {
      return (
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="bg-surface-L2 rounded-2xl p-4">
            <Camera className="size-8 text-content-brand-bold" />
          </div>
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-base font-semibold text-content-primary-bold leading-6">
              Photograph the flight folio
            </p>
            <p className="text-sm text-content-primary-subtle leading-5">
              Lay the technical log flat, fill the frame, and avoid glare. The fields are read
              automatically.
            </p>
          </div>
          {uploadError && (
            <p className="text-sm text-content-negative leading-5">{uploadError}</p>
          )}
          {isUploading ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="size-4 animate-spin text-content-primary-subtle" />
              <p className="text-sm text-content-primary-subtle leading-5">
                Uploading… {uploadProgress ?? 100}%
              </p>
            </div>
          ) : (
            <div className="flex w-full flex-col gap-2">
              <Button variant="primary" size="sm" onClick={() => cameraInputRef.current?.click()}>
                Take photo
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => libraryInputRef.current?.click()}
              >
                Choose from library
              </Button>
            </div>
          )}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              void handleFileSelected(e.target.files?.[0])
              e.target.value = ''
            }}
          />
          <input
            ref={libraryInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              void handleFileSelected(e.target.files?.[0])
              e.target.value = ''
            }}
          />
        </div>
      )
    }

    // 4. Extraction in progress
    if (!scan || scan.status === 'processing') {
      return (
        <div className="flex flex-col items-center gap-3 py-10">
          <Loader2 className="size-6 animate-spin text-content-primary-subtle" />
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-base font-semibold text-content-primary-bold leading-6">
              Reading the folio…
            </p>
            <p className="text-sm text-content-primary-subtle leading-5">
              This can take up to a minute. You can keep the app open.
            </p>
          </div>
        </div>
      )
    }

    // 6. Failed
    if (scan.status === 'failed' || !scan.extracted) {
      return (
        <div className="flex flex-col items-center gap-3 py-10">
          <TriangleAlert className="size-6 text-content-negative" />
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-base font-semibold text-content-primary-bold leading-6">
              Couldn't read the folio
            </p>
            <p className="text-sm text-content-primary-subtle leading-5">
              {scan.errorMessage ?? 'Something went wrong while reading the folio'}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleRetry()}
            disabled={retryScan.isPending}
          >
            Retry
          </Button>
        </div>
      )
    }

    // 5. Done — the extracted sheet as tables
    return <FlightFolioScanResult extracted={scan.extracted} />
  }

  const showResultFooter = !!scan && scan.status !== 'processing'

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className="flex flex-row items-center justify-between">
          <DrawerTitle className="text-content-primary-bold">Scan flight folio</DrawerTitle>
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            className="size-8"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" />
          </Button>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4">{renderBody()}</div>

        {showResultFooter && (
          <DrawerFooter className="flex-row">
            <Button
              variant="destructive"
              size="sm"
              className="flex-1"
              onClick={() => void handleDelete()}
              disabled={deleteScan.isPending}
            >
              Delete
            </Button>
            {onApply && scan?.status === 'completed' && scan.extracted ? (
              <Button
                variant="primary"
                size="sm"
                className="flex-1"
                onClick={() => {
                  hapticLight()
                  onApply(scan.extracted as FolioExtraction)
                  onOpenChange(false)
                }}
              >
                Apply to log
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Done
              </Button>
            )}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  )
}
